/**
 * @fileoverview Unified message component for displaying contextual feedback
 * @module ctl-message
 * @version 1.0.0
 * 
 * @description
 * A unified message component that replaces ctl-alert, ctl-banner, ctl-callout, and ctl-inline-message.
 * Supports multiple display variants (alert, banner, callout, inline) with consistent API.
 * 
 * @example
 * <!-- Alert variant (block with border) -->
 * <ctl-message variant="alert" tone="success">
 *   Your profile has been updated successfully.
 * </ctl-message>
 * 
 * @example
 * <!-- Banner variant (full-width sticky) -->
 * <ctl-message variant="banner" tone="warning" sticky>
 *   Trial expires in 5 days.
 * </ctl-message>
 * 
 * @example
 * <!-- Callout variant (block with left border) -->
 * <ctl-message variant="callout" tone="info">
 *   <ctl-icon slot="icon" name="info"></ctl-icon>
 *   This is an informational callout.
 * </ctl-message>
 * 
 * @example
 * <!-- Inline variant (flows with text) -->
 * <ctl-message variant="inline" tone="danger" size="sm">
 *   Required field
 * </ctl-message>
 */

import { dispatchEvent } from '../../../utils/events.js';
import { shadowQuery } from '../../../utils/dom.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    display: block;
    position: relative;
    background: var(--ctl-color-surface);
    color: var(--ctl-color-text);
    border: 1px solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-lg);
    padding: var(--ctl-space-3) var(--ctl-space-4);
    font: 400 14px var(--ctl-font-sans, system-ui);
    line-height: 1.5;
  }

  /* ===== VARIANT: ALERT ===== */
  /* Default variant - block element with border and padding */
  :host([variant="alert"]) {
    display: block;
  }

  /* Alert tone colors - solid */
  :host([variant="alert"][style-variant="solid"][tone="info"]) {
    background: var(--ctl-color-info);
    color: var(--ctl-color-on-info);
    border-color: color-mix(in srgb, var(--ctl-color-info) 78%, black);
  }
  :host([variant="alert"][style-variant="solid"][tone="success"]) {
    background: var(--ctl-color-success);
    color: var(--ctl-color-on-success);
    border-color: color-mix(in srgb, var(--ctl-color-success) 78%, black);
  }
  :host([variant="alert"][style-variant="solid"][tone="warning"]) {
    background: var(--ctl-color-warning);
    color: var(--ctl-color-on-warning);
    border-color: color-mix(in srgb, var(--ctl-color-warning) 78%, black);
  }
  :host([variant="alert"][style-variant="solid"][tone="danger"]) {
    background: var(--ctl-color-danger);
    color: var(--ctl-color-on-danger);
    border-color: color-mix(in srgb, var(--ctl-color-danger) 78%, black);
  }

  /* Alert tone colors - soft (default) */
  :host([variant="alert"]:not([style-variant]), [variant="alert"][style-variant="soft"]) {
    background: var(--ctl-color-surface);
  }
  :host([variant="alert"][style-variant="soft"][tone="info"]) {
    background: color-mix(in srgb, var(--ctl-color-info) 12%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-info) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-info) 25%, var(--ctl-color-border));
  }
  :host([variant="alert"][style-variant="soft"][tone="success"]) {
    background: color-mix(in srgb, var(--ctl-color-success) 12%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-success) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-success) 25%, var(--ctl-color-border));
  }
  :host([variant="alert"][style-variant="soft"][tone="warning"]) {
    background: color-mix(in srgb, var(--ctl-color-warning) 12%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-warning) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-warning) 25%, var(--ctl-color-border));
  }
  :host([variant="alert"][style-variant="soft"][tone="danger"]) {
    background: color-mix(in srgb, var(--ctl-color-danger) 12%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-danger) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-danger) 25%, var(--ctl-color-border));
  }

  /* Alert tone colors - outline */
  :host([variant="alert"][style-variant="outline"]) {
    background: transparent;
  }
  :host([variant="alert"][style-variant="outline"][tone="info"]) {
    color: color-mix(in srgb, var(--ctl-color-info) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-info) 45%, var(--ctl-color-border));
  }
  :host([variant="alert"][style-variant="outline"][tone="success"]) {
    color: color-mix(in srgb, var(--ctl-color-success) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-success) 45%, var(--ctl-color-border));
  }
  :host([variant="alert"][style-variant="outline"][tone="warning"]) {
    color: color-mix(in srgb, var(--ctl-color-warning) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-warning) 45%, var(--ctl-color-border));
  }
  :host([variant="alert"][style-variant="outline"][tone="danger"]) {
    color: color-mix(in srgb, var(--ctl-color-danger) 86%, var(--ctl-color-text));
    border-color: color-mix(in srgb, var(--ctl-color-danger) 45%, var(--ctl-color-border));
  }

  /* ===== VARIANT: BANNER ===== */
  /* Full-width element with bottom border, hidden by default */
  :host([variant="banner"]) {
    display: none;
    width: 100%;
    border: none;
    border-bottom: 1px solid var(--ctl-color-border);
    border-radius: 0;
    padding: var(--ctl-space-3) var(--ctl-space-4);
    z-index: var(--ctl-z-dropdown);
  }

  :host([variant="banner"][open]) {
    display: block;
  }

  :host([variant="banner"][sticky]) {
    position: sticky;
    top: 0;
  }

  /* Banner tone colors */
  :host([variant="banner"]:not([tone]), [variant="banner"][tone="neutral"]) {
    background: var(--ctl-color-surface);
    border-bottom-color: var(--ctl-color-border);
  }
  :host([variant="banner"][tone="info"]) {
    background: color-mix(in srgb, var(--ctl-color-info) 10%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-info) 86%, var(--ctl-color-text));
    border-bottom-color: color-mix(in srgb, var(--ctl-color-info) 30%, var(--ctl-color-border));
  }
  :host([variant="banner"][tone="success"]) {
    background: color-mix(in srgb, var(--ctl-color-success) 10%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-success) 86%, var(--ctl-color-text));
    border-bottom-color: color-mix(in srgb, var(--ctl-color-success) 30%, var(--ctl-color-border));
  }
  :host([variant="banner"][tone="warning"]) {
    background: color-mix(in srgb, var(--ctl-color-warning) 10%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-warning) 86%, var(--ctl-color-text));
    border-bottom-color: color-mix(in srgb, var(--ctl-color-warning) 30%, var(--ctl-color-border));
  }
  :host([variant="banner"][tone="danger"]) {
    background: color-mix(in srgb, var(--ctl-color-danger) 10%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-danger) 86%, var(--ctl-color-text));
    border-bottom-color: color-mix(in srgb, var(--ctl-color-danger) 30%, var(--ctl-color-border));
  }

  /* ===== VARIANT: CALLOUT ===== */
  /* Block element with thick left border */
  :host([variant="callout"]) {
    display: block;
    border-left-width: var(--spacing-xxs, 4px);
    padding: var(--spacing-md, 16px) var(--spacing-lg, 20px);
    line-height: var(--line-height-relaxed, 1.6);
  }

  /* Callout tone colors */
  :host([variant="callout"]:not([tone]), [variant="callout"][tone="neutral"]) {
    border-color: var(--ctl-color-border);
  }
  :host([variant="callout"][tone="info"]) {
    background: color-mix(in srgb, var(--ctl-color-info) 5%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-info) 86%, var(--ctl-color-text));
    border-color: var(--ctl-color-info);
  }
  :host([variant="callout"][tone="success"]) {
    background: color-mix(in srgb, var(--ctl-color-success) 5%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-success) 86%, var(--ctl-color-text));
    border-color: var(--ctl-color-success);
  }
  :host([variant="callout"][tone="warning"]) {
    background: color-mix(in srgb, var(--ctl-color-warning) 5%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-warning) 86%, var(--ctl-color-text));
    border-color: var(--ctl-color-warning);
  }
  :host([variant="callout"][tone="danger"]) {
    background: color-mix(in srgb, var(--ctl-color-danger) 5%, var(--ctl-color-surface));
    color: color-mix(in srgb, var(--ctl-color-danger) 86%, var(--ctl-color-text));
    border-color: var(--ctl-color-danger);
  }

  /* ===== VARIANT: INLINE ===== */
  /* Inline-flex element that flows with text */
  :host([variant="inline"]) {
    display: inline-flex;
    align-items: center;
    gap: var(--spacing-xs, 8px);
    background: transparent;
    border: none;
    padding: 0;
    line-height: var(--line-height-normal, 1.4);
  }

  /* Inline size variants */
  :host([variant="inline"][size="sm"]) {
    font-size: var(--font-size-xs, 0.75rem);
  }
  :host([variant="inline"][size="md"]) {
    font-size: var(--font-size-xs, 0.75rem);
  }

  /* Inline tone colors */
  :host([variant="inline"]:not([tone]), [variant="inline"][tone="neutral"]) {
    color: var(--ctl-color-muted);
  }
  :host([variant="inline"][tone="info"]) {
    color: color-mix(in srgb, var(--ctl-color-info) 86%, var(--ctl-color-text));
  }
  :host([variant="inline"][tone="success"]) {
    color: color-mix(in srgb, var(--ctl-color-success) 86%, var(--ctl-color-text));
  }
  :host([variant="inline"][tone="warning"]) {
    color: color-mix(in srgb, var(--ctl-color-warning) 86%, var(--ctl-color-text));
  }
  :host([variant="inline"][tone="danger"]) {
    color: color-mix(in srgb, var(--ctl-color-danger) 86%, var(--ctl-color-text));
  }

  /* ===== SHARED LAYOUT STYLES ===== */
  .message-container {
    display: flex;
    gap: var(--ctl-space-3);
    align-items: start;
    flex-wrap: wrap;
  }

  /* Banner uses centered container with max-width */
  :host([variant="banner"]) .message-container {
    max-width: 1400px;
    margin: 0 auto;
    align-items: center;
    flex-wrap: nowrap;
  }

  /* Inline doesn't use container structure */
  :host([variant="inline"]) .message-container {
    display: contents;
  }

  /* Callout has slightly different gap */
  :host([variant="callout"]) .message-container {
    gap: var(--spacing-sm, 12px);
  }

  .message-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
    font-size: var(--font-size-lg, 1.125rem);
  }

  :host([variant="callout"]) .message-icon {
    font-size: var(--font-size-xl, 1.5rem);
    margin-top: var(--spacing-xxs, 2px);
  }

  /* Hide icon container when no content is slotted */
  .message-icon:not([data-has-content]) {
    display: none;
  }

  .message-content {
    flex: 1;
    min-width: 0;
  }

  :host([variant="inline"]) .message-content {
    display: inline;
  }

  .message-title {
    font: var(--font-weight-semibold, 600) var(--font-size-sm, 0.875rem) var(--ctl-font-sans, system-ui);
    margin: 0 0 var(--ctl-space-2);
  }

  :host([variant="callout"]) .message-title {
    font: 650 var(--font-size-md, 1rem) var(--ctl-font-sans, system-ui);
    margin: 0 0 var(--spacing-xs, 8px);
  }

  /* Hide title container when no content is slotted */
  .message-title:not([data-has-content]) {
    display: none;
  }

  .message-body {
    display: block;
  }

  .message-actions {
    display: flex;
    gap: var(--ctl-space-2);
    margin-top: var(--ctl-space-3);
    /* For alert/callout variants, actions should appear on a new row below content */
    flex-basis: 100%;
    order: 3;
  }

  :host([variant="banner"]) .message-actions {
    flex-shrink: 0;
    gap: var(--spacing-xs, 8px);
    margin-top: 0;
    align-items: center;
    /* For banner, actions are inline siblings, not on a new row */
    flex-basis: auto;
    order: 0;
  }

  :host([variant="callout"]) .message-actions {
    gap: var(--spacing-xs, 8px);
    margin-top: var(--spacing-sm, 12px);
  }

  /* Hide actions container when no content is slotted */
  .message-actions:not([data-has-content]) {
    display: none;
  }

  .close-btn {
    flex-shrink: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: var(--ctl-space-1);
    line-height: 1;
    opacity: var(--opacity-60, 0.6);
    transition: opacity var(--ctl-transition-normal);
    color: currentColor;
    font-size: var(--font-size-lg, 1.125rem);
    align-self: start;
  }

  :host([variant="banner"]) .close-btn {
    padding: var(--spacing-xs, 8px);
    transition: opacity var(--transition-normal, 300ms ease-in-out);
    align-self: center;
  }

  .close-btn:hover {
    opacity: 1;
  }

  :host(:not([closeable])) .close-btn {
    display: none;
  }

  /* Hide structural elements for inline variant */
  :host([variant="inline"]) .message-icon,
  :host([variant="inline"]) .message-title,
  :host([variant="inline"]) .message-actions,
  :host([variant="inline"]) .close-btn {
    display: none;
  }
</style>

<div class="message-container" part="container">
  <div class="message-icon" part="icon">
    <slot name="icon"></slot>
  </div>
  <div class="message-content" part="content">
    <div class="message-title" part="title">
      <slot name="title"></slot>
    </div>
    <div class="message-body" part="body">
      <slot></slot>
    </div>
  </div>
  <div class="message-actions" part="actions">
    <slot name="actions"></slot>
  </div>
  <button type="button" class="close-btn" aria-label="Close" part="close-button">×</button>
</div>
`;

/**
 * Unified Message Web Component
 * 
 * @class CtlMessage
 * @extends HTMLElement
 * 
 * @attr {string} variant - Display variant: "alert" | "banner" | "callout" | "inline" (default: "alert")
 * @attr {string} tone - Semantic tone: "neutral" | "info" | "success" | "warning" | "danger" (default varies by variant)
 * @attr {string} style-variant - Style variant for alert: "soft" | "solid" | "outline" (default: "soft", only for variant="alert")
 * @attr {string} title - Optional message title (for alert, callout variants)
 * @attr {boolean} closeable - Makes message dismissible with close button
 * @attr {boolean} open - Controls visibility (for banner variant)
 * @attr {boolean} sticky - Makes banner sticky at top (only for variant="banner")
 * @attr {string} size - Size variant: "sm" | "md" (only for variant="inline")
 * 
 * @slot - Default slot for message content
 * @slot icon - Leading icon slot (not visible for inline variant)
 * @slot title - Title content slot (not visible for inline variant)
 * @slot actions - Action buttons or links slot (not visible for inline variant)
 * 
 * @fires ctl-close - Dispatched when the message is closed
 * @fires ctl-open - Dispatched when banner is opened (variant="banner" only)
 * 
 * @csspart container - The main message container
 * @csspart icon - The icon wrapper
 * @csspart title - The title element
 * @csspart content - The content wrapper
 * @csspart body - The body content element
 * @csspart actions - The actions container
 * @csspart close-button - The close button
 */
export class CtlMessage extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
  }

  static get observedAttributes() {
    return ["variant", "tone", "style-variant", "title", "closeable", "open", "sticky", "size"];
  }

  connectedCallback() {
    this._closeHandler = () => this.close();
    const closeBtn = shadowQuery(this, ".close-btn");
    closeBtn.addEventListener("click", this._closeHandler);

    // Set defaults based on variant
    if (!this.hasAttribute("variant")) this.setAttribute("variant", "alert");
    this._applyVariantDefaults();

    // Set title element if title attribute exists
    this._updateTitle();

    // Set role for accessibility
    this._updateRole();

    // Track slot content changes
    this._setupSlotTracking();
  }

  disconnectedCallback() {
    const closeBtn = shadowQuery(this, ".close-btn");
    if (closeBtn && this._closeHandler) {
      closeBtn.removeEventListener("click", this._closeHandler);
    }

    // Clean up slot listeners
    if (this._slotListeners) {
      this._slotListeners.forEach(({ slot, handler }) => {
        slot.removeEventListener("slotchange", handler);
      });
      this._slotListeners = null;
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "variant" && oldValue !== newValue) {
      this._applyVariantDefaults();
      this._updateRole();
    } else if (name === "title") {
      this._updateTitle();
    } else if (name === "tone" && oldValue !== newValue) {
      this._updateRole();
    } else if (name === "open" && this.getAttribute("variant") === "banner") {
      if (newValue !== null && oldValue === null) {
        dispatchEvent(this, "ctl-open");
      } else if (newValue === null && oldValue !== null) {
        dispatchEvent(this, "ctl-close");
      }
    }
  }

  _applyVariantDefaults() {
    const variant = this.getAttribute("variant");
    
    if (variant === "alert") {
      if (!this.hasAttribute("tone")) this.setAttribute("tone", "info");
      if (!this.hasAttribute("style-variant")) this.setAttribute("style-variant", "soft");
    } else if (variant === "banner") {
      if (!this.hasAttribute("tone")) this.setAttribute("tone", "info");
    } else if (variant === "callout") {
      if (!this.hasAttribute("tone")) this.setAttribute("tone", "neutral");
    } else if (variant === "inline") {
      if (!this.hasAttribute("tone")) this.setAttribute("tone", "neutral");
      if (!this.hasAttribute("size")) this.setAttribute("size", "sm");
    }
  }

  _updateRole() {
    const variant = this.getAttribute("variant");
    const tone = this.getAttribute("tone");

    // Only apply ARIA roles for variants that historically had them (alert + banner).
    // For callout/inline variants, preserve legacy behavior by not setting a role.
    if (variant === "alert" || variant === "banner") {
      // Danger and warning tones require immediate attention, so use role="alert"
      // Info, success, and neutral provide status updates, so use role="status"
      if (tone === "danger" || tone === "warning") {
        this.setAttribute("role", "alert");
      } else {
        // Applies to: info, success, neutral, and any other tone values
        this.setAttribute("role", "status");
      }
    } else {
      // For callout, inline, and any other variants, remove any previously-set role.
      this.removeAttribute("role");
    }
  }

  _setupSlotTracking() {
    this._slotListeners = [];
    const slotsToTrack = [
      { selector: 'slot[name="icon"]', container: ".message-icon" },
      { selector: 'slot[name="actions"]', container: ".message-actions" }
      // Note: title slot is handled separately below to account for attribute-based titles
    ];

    slotsToTrack.forEach(({ selector, container }) => {
      const slot = shadowQuery(this, selector);
      const containerEl = shadowQuery(this, container);
      
      if (slot && containerEl) {
        const handler = () => {
          const hasContent = slot.assignedNodes({ flatten: true }).length > 0;
          if (hasContent) {
            containerEl.setAttribute("data-has-content", "");
          } else {
            containerEl.removeAttribute("data-has-content");
          }
        };
        
        slot.addEventListener("slotchange", handler);
        this._slotListeners.push({ slot, handler });
        
        // Initial check
        handler();
      }
    });

    // Track the title slot separately so that _updateTitle() is re-run
    // whenever slotted title content is added or removed.
    const titleSlot = shadowQuery(this, 'slot[name="title"]');
    if (titleSlot) {
      const titleHandler = () => {
        this._updateTitle();
      };
      titleSlot.addEventListener("slotchange", titleHandler);
      this._slotListeners.push({ slot: titleSlot, handler: titleHandler });
      // Initial sync so the title state matches current slotted/attribute content
      titleHandler();
    }
  }

  _updateTitle() {
    const titleAttr = this.getAttribute("title");
    const titleContainer = shadowQuery(this, ".message-title");
    
    if (!titleContainer) return;

    // Check if there's slotted content
    const titleSlot = shadowQuery(this, 'slot[name="title"]');
    const hasSlottedContent = titleSlot && titleSlot.assignedNodes({ flatten: true }).length > 0;
    
    if (hasSlottedContent) {
      // If there's slotted content, remove any existing attribute-based
      // fallback text so it doesn't render alongside the slotted title.
      const existingTitleText = titleContainer.querySelector(".title-text");
      if (existingTitleText) {
        existingTitleText.remove();
      }
      // Mark the container as having content via the slot.
      titleContainer.setAttribute("data-has-content", "");
      return;
    }
    
    // No slotted content, so we can set the attribute-based title
    // Find or create a span for the title text that doesn't interfere with the slot
    let titleText = titleContainer.querySelector(".title-text");
    if (!titleText) {
      titleText = document.createElement("span");
      titleText.className = "title-text";
      // Insert before the slot so it appears first
      titleContainer.insertBefore(titleText, titleSlot);
    }
    
    titleText.textContent = titleAttr || "";
    
    // Update the container's data attribute based on whether we have content
    if (titleAttr) {
      titleContainer.setAttribute("data-has-content", "");
    } else {
      titleContainer.removeAttribute("data-has-content");
    }
  }

  /**
   * Closes the message
   * @param {string} reason - The reason for closing (only used for non-banner variants)
   * 
   * For banner variant: removes the 'open' attribute to hide the banner
   * For other variants: removes the element from the DOM and dispatches ctl-close event with reason
   */
  close(reason = "user") {
    const variant = this.getAttribute("variant");
    
    if (variant === "banner") {
      // Banner: remove 'open' attribute (reason parameter not used for banner)
      if (!this.hasAttribute("open")) return;
      this.removeAttribute("open");
    } else {
      // All other variants: remove element and dispatch event with reason
      dispatchEvent(this, "ctl-close", { reason });
      this.remove();
    }
  }

  /**
   * Opens the message (banner variant only)
   * 
   * Note: This method only applies to the banner variant.
   * Calling it on other variants will have no effect.
   */
  open() {
    const variant = this.getAttribute("variant");
    
    if (variant === "banner") {
      if (this.hasAttribute("open")) return;
      this.setAttribute("open", "");
    }
    // Silently ignore for non-banner variants (expected behavior)
  }
}

if (!customElements.get("ctl-message")) {
  customElements.define("ctl-message", CtlMessage);
}
